import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { friendlyAuthError, parseRateLimit } from "@/lib/auth-errors";
import type { AnalyticsEvent } from "@/lib/analytics";

const searchSchema = z.object({
  mode: z.enum(["signin", "signup", "forgot"]).optional(),
  email: z.string().optional(),
  // Same-origin path only. Rejecting absolute URLs and protocol-relative
  // paths keeps this from becoming an open-redirect.
  redirect: z
    .string()
    .regex(/^\/(?!\/)[A-Za-z0-9\-._~!$&'()*+,;=:@%/?]*$/)
    .max(300)
    .optional(),
});

export const Route = createFileRoute("/auth")({
  validateSearch: (search) => searchSchema.parse(search),
  head: () => ({
    meta: [
      { title: "Sign in · Buddha KA" },
      { name: "description", content: "Sign in to the Buddha KA + KAfrequency admin field." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: AuthPage,
});

type Mode = "signin" | "signup" | "forgot";

/** Same-origin redirect targets — works on preview and on buddhaka.com alike. */
function origin(): string {
  return typeof window !== "undefined" ? window.location.origin : "";
}

function AuthPage() {
  const navigate = useNavigate();
  const search = Route.useSearch();
  const [mode, setMode] = useState<Mode>(search.mode ?? "signin");
  const [email, setEmail] = useState(search.email ?? "");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [showRecovery, setShowRecovery] = useState(false);
  const [needsConfirm, setNeedsConfirm] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const cooldownTimer = useRef<ReturnType<typeof setInterval> | null>(null);
  // Where to land after a successful sign-in. Defaults to the account hub.
  const afterAuth = search.redirect ?? "/account";

  useEffect(() => {
    supabase.auth.getUser().then(({ data }) => {
      if (data.user) navigate({ to: afterAuth });
    });
  }, [navigate, afterAuth]);

  useEffect(() => {
    return () => {
      if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    };
  }, []);

  function resetMessages() {
    setError(null);
    setInfo(null);
    setShowRecovery(false);
    setNeedsConfirm(false);
  }

  function startCooldown(seconds: number) {
    setCooldown(seconds);
    if (cooldownTimer.current) clearInterval(cooldownTimer.current);
    cooldownTimer.current = setInterval(() => {
      setCooldown((s) => {
        if (s <= 1) {
          if (cooldownTimer.current) clearInterval(cooldownTimer.current);
          return 0;
        }
        return s - 1;
      });
    }, 1000);
  }

  function hashEmail(v: string): string {
    let h = 5381;
    const s = v.trim().toLowerCase();
    for (let i = 0; i < s.length; i++) h = ((h << 5) + h) ^ s.charCodeAt(i);
    return `h${(h >>> 0).toString(36)}`;
  }

  function track(event: AnalyticsEvent, props: Record<string, string | number | boolean | null | undefined>) {
    void import("@/lib/analytics").then((m) => m.track(event, props)).catch(() => {});
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (cooldown > 0) return;
    resetMessages();
    setBusy(true);
    const cleanEmail = email.trim().toLowerCase();
    try {
      if (mode === "signin") {
        const { error } = await supabase.auth.signInWithPassword({ email: cleanEmail, password });
        if (error) throw error;
        track("auth_signin_success", { email_hash: hashEmail(cleanEmail) });
        navigate({ to: afterAuth });
      } else if (mode === "signup") {
        const { data, error } = await supabase.auth.signUp({
          email: cleanEmail,
          password,
          options: { emailRedirectTo: `${origin()}/account` },
        });
        if (error) throw error;
        track("auth_signup", { email_hash: hashEmail(cleanEmail) });
        if (data.session) {
          navigate({ to: afterAuth });
        } else {
          setNeedsConfirm(true);
          setInfo(
            "Check your inbox (and spam) for a confirmation link. It expires quickly — open it on this device. Once confirmed, come back and sign in.",
          );
        }
      } else {
        const { error } = await supabase.auth.resetPasswordForEmail(cleanEmail, {
          redirectTo: `${origin()}/reset-password`,
        });
        if (error) throw error;
        track("auth_password_reset_requested", { email_hash: hashEmail(cleanEmail) });
        setInfo(
          "If an account exists for that email, a reset link is on its way. The link expires in one hour — open it on this device.",
        );
      }
    } catch (err) {
      const rate = parseRateLimit(err);
      if (rate.isRateLimited) {
        startCooldown(rate.retryAfterSec);
        setError("Too many attempts. For your security, please wait before trying again.");
      } else {
        const raw = (err instanceof Error ? err.message : "").toLowerCase();
        setError(friendlyAuthError(err));
        if (mode === "signin") {
          if (raw.includes("email not confirmed")) setNeedsConfirm(true);
          else if (raw.includes("invalid login credentials") || raw.includes("invalid_credentials")) {
            setShowRecovery(true);
          }
        }
      }
      track("auth_signin_failed", {
        mode,
        email_hash: hashEmail(cleanEmail),
        rate_limited: rate.isRateLimited,
      });
    } finally {
      setBusy(false);
    }
  }

  async function resendConfirmation() {
    if (!email.trim()) {
      setError("Enter your email first.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { error } = await supabase.auth.resend({
        type: "signup",
        email: email.trim().toLowerCase(),
        options: { emailRedirectTo: `${origin()}/account` },
      });
      if (error) throw error;
      setInfo("If that email needs confirming, a fresh link is on its way.");
    } catch (err) {
      const rate = parseRateLimit(err);
      if (rate.isRateLimited) {
        startCooldown(rate.retryAfterSec);
        setError("Too many email requests. Please wait before asking for another link.");
      } else {
        setError(friendlyAuthError(err));
      }
    } finally {
      setBusy(false);
    }
  }

  const heading =
    mode === "signin" ? "Sign in." : mode === "signup" ? "Create an account." : "Reset your password.";

  return (
    <div className="mx-auto flex min-h-[80vh] max-w-md flex-col justify-center px-4 py-16 sm:px-6">
      <p className="text-xs uppercase tracking-[0.3em] text-gold">Enter the field</p>
      <h1 className="mt-3 font-serif text-4xl">{heading}</h1>
      <p className="mt-3 text-sm text-muted-foreground">
        {mode === "forgot"
          ? "We'll email a secure link so you can choose a new password."
          : "A newsletter subscription is not an account. If you only ever subscribed to emails, create an account below with the same address — nothing is lost."}
      </p>

      <form onSubmit={onSubmit} className="mt-8 space-y-4">
        <label className="block">
          <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Email</span>
          <input
            type="email"
            required
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-2 w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
          />
        </label>

        {mode !== "forgot" && (
          <label className="block">
            <span className="text-xs uppercase tracking-[0.25em] text-muted-foreground">Password</span>
            <input
              type="password"
              required
              minLength={8}
              autoComplete={mode === "signin" ? "current-password" : "new-password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="mt-2 w-full rounded-md border hairline bg-transparent px-3 py-2 text-sm outline-none focus:border-gold"
            />
          </label>
        )}

        {error && <p className="text-sm text-red-400">{error}</p>}
        {info && <p className="text-sm text-emerald-400">{info}</p>}

        {(showRecovery || needsConfirm) && (
          <div className="rounded-md border hairline bg-white/[0.02] p-4 text-xs leading-relaxed text-muted-foreground">
            <p className="text-[0.7rem] uppercase tracking-[0.2em] text-gold">Can't get in?</p>
            <ul className="mt-3 space-y-2">
              {needsConfirm && (
                <li>
                  Waiting on a confirmation link?{" "}
                  <button
                    type="button"
                    onClick={resendConfirmation}
                    className="text-gold underline underline-offset-4"
                  >
                    Resend confirmation email
                  </button>
                </li>
              )}
              <li>
                Forgot your password?{" "}
                <button
                  type="button"
                  onClick={() => {
                    resetMessages();
                    setMode("forgot");
                  }}
                  className="text-gold underline underline-offset-4"
                >
                  Send a reset link
                </button>
              </li>
              <li>
                Only ever joined the newsletter with this address?{" "}
                <button
                  type="button"
                  onClick={() => {
                    resetMessages();
                    setMode("signup");
                  }}
                  className="text-gold underline underline-offset-4"
                >
                  Create an account using this email
                </button>
              </li>
            </ul>
          </div>
        )}

        {cooldown > 0 && (
          <div
            role="status"
            aria-live="polite"
            className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-3 text-xs text-amber-200"
          >
            <div className="flex items-center justify-between gap-3">
              <span className="uppercase tracking-[0.2em]">Cooldown active</span>
              <span className="font-mono text-sm text-amber-100">{cooldown}s</span>
            </div>
            <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-amber-500/15">
              <div
                className="h-full bg-amber-400/70 transition-all duration-1000 ease-linear"
                style={{ width: `${Math.max(0, Math.min(100, (cooldown / 60) * 100))}%` }}
              />
            </div>
          </div>
        )}

        <button
          type="submit"
          disabled={busy || cooldown > 0}
          className="w-full rounded-full bg-gold-gradient px-5 py-2.5 text-sm font-semibold text-obsidian disabled:opacity-60"
        >
          {cooldown > 0
            ? `Retry in ${cooldown}s`
            : busy
            ? "Working…"
            : mode === "signin"
            ? "Sign in"
            : mode === "signup"
            ? "Create account"
            : "Send reset link"}
        </button>
      </form>

      <div className="mt-6 flex flex-col gap-3 text-xs uppercase tracking-[0.25em] text-muted-foreground">
        {mode !== "forgot" && (
          <button
            type="button"
            onClick={() => {
              resetMessages();
              setMode(mode === "signin" ? "signup" : "signin");
            }}
            className="text-left hover:text-gold"
          >
            {mode === "signin" ? "No account? Create one" : "Already have an account? Sign in"}
          </button>
        )}
        {mode === "signin" && (
          <button
            type="button"
            onClick={() => {
              resetMessages();
              setMode("forgot");
            }}
            className="text-left hover:text-gold"
          >
            Forgot password?
          </button>
        )}
        {mode === "forgot" && (
          <button
            type="button"
            onClick={() => {
              resetMessages();
              setMode("signin");
            }}
            className="text-left hover:text-gold"
          >
            Back to sign in
          </button>
        )}
        <Link to="/newsletter" className="hover:text-gold">
          Newsletter only? Manage subscription
        </Link>
      </div>
    </div>
  );
}
